import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  alpha,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';

import { countNow, describeManageError, fieldOfError, manageErrorCode } from '../manage/manage';
import { checkListForm, LIST_LIMITS, listCreateVariables, ListForm, EMPTY_LIST_FORM } from '../lists/listManager';
import { CRM_SAVE_FILTER, CRM_SAVE_FILTER_AS_LIST } from '../lists/lists.queries';
import { people } from '../tagging/tagging';
import { CRM_SELECTION_COUNT } from '../tagging/tagging.queries';
import { CrmContactFilter } from './contactList';

type Kind = 'filter' | 'list';

interface SaveFilterDialogProps {
  filter: CrmContactFilter;
  onClose: () => void;
  /** After the API has saved it: its name, and which kind. */
  onSaved: (name: string, kind: Kind, count: number) => void;
}

const KINDS: Array<{ kind: Kind; title: string; icon: React.ReactNode; tone: string; text: (who: string) => string }> = [
  {
    kind: 'filter',
    title: 'Saved filter',
    icon: <FilterAltOutlinedIcon />,
    tone: '#6a1b9a',
    text: () => 'Stays up to date: it reaches whoever matches when it is used. Nobody is added by hand.',
  },
  {
    kind: 'list',
    title: 'List',
    icon: <ListAltOutlinedIcon />,
    tone: '#2e7d32',
    text: (who) => `A snapshot of the ${who} who match now. They stay on it when they stop matching, and people can be added or taken off by hand.`,
  },
];

/**
 * Saves the contact list's filter, NationBuilder's two ways: as a saved filter
 * (run again whenever it is used) or as a list (the people who match now). A
 * list is made only on the count confirmed here, counted afresh, never the
 * list's estimate; when it moved, the new number is shown to confirm again.
 */
export const SaveFilterDialog = ({ filter, onClose, onSaved }: SaveFilterDialogProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [kind, setKind] = useState<Kind>('filter');
  const [form, setForm] = useState<ListForm>(EMPTY_LIST_FORM);
  const [tried, setTried] = useState(false);
  const [refusal, setRefusal] = useState<unknown>();
  const [recount, setRecount] = useState<number>();

  const counted = useQuery<{ crmContactCounts: { total: number } }>(CRM_SELECTION_COUNT, {
    variables: { filter },
    fetchPolicy: 'network-only',
  });
  const count = recount ?? counted.data?.crmContactCounts.total;

  const [saveFilter, savingFilter] = useMutation(CRM_SAVE_FILTER);
  const [saveList, savingList] = useMutation(CRM_SAVE_FILTER_AS_LIST);
  const running = savingFilter.loading || savingList.loading;

  const problems = checkListForm(form);
  const refusedField = refusal === undefined ? undefined : fieldOfError(refusal);
  const refusedText = refusal === undefined ? undefined : describeManageError(refusal, 'list');
  const errorOf = (field: keyof ListForm) => (refusedField === field ? refusedText : tried ? problems[field] : undefined);

  const save = async () => {
    setTried(true);
    setRefusal(undefined);
    if (Object.keys(problems).length || count === undefined) return;
    const fields = listCreateVariables(form);
    try {
      if (kind === 'filter') await saveFilter({ variables: { ...fields, filter } });
      else await saveList({ variables: { ...fields, filter, expectedCount: count } });
      onSaved(fields.name, kind, count);
    } catch (err) {
      const now = countNow(err);
      if (now !== undefined) setRecount(now);
      setRefusal(err);
    }
  };

  const who = count === undefined ? 'people' : people(count);
  const changed = manageErrorCode(refusal) === 'CRM_SELECTION_CHANGED';

  return (
    <Dialog open onClose={running ? undefined : onClose} fullScreen={phone} fullWidth maxWidth="sm" aria-labelledby="crm-save-filter-title">
      <DialogTitle id="crm-save-filter-title">Save this filter</DialogTitle>
      <Box
        component="form"
        noValidate
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          save();
        }}
      >
        <DialogContent dividers sx={{ display: 'grid', gap: 2.5 }}>
          <Typography variant="body2" color="text.secondary" aria-live="polite">
            {count === undefined
              ? counted.error
                ? 'Could not count the people this filter matches.'
                : 'Counting the people this filter matches…'
              : `It matches ${people(count)} now.`}
          </Typography>

          <Box role="radiogroup" aria-label="Save as" sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {KINDS.map((option) => {
              const on = kind === option.kind;
              return (
                <ButtonBase
                  key={option.kind}
                  role="radio"
                  aria-checked={on}
                  aria-label={option.title}
                  onClick={() => {
                    setKind(option.kind);
                    setRefusal(undefined);
                  }}
                  disabled={running}
                  sx={{
                    display: 'grid',
                    gap: 0.75,
                    justifyItems: 'start',
                    textAlign: 'left',
                    p: 1.75,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: on ? alpha(option.tone, 0.6) : 'divider',
                    bgcolor: on ? alpha(option.tone, 0.06) : 'background.paper',
                    boxShadow: on ? `0 0 0 1px ${alpha(option.tone, 0.4)}` : 'none',
                    '&.Mui-focusVisible': { outline: '2px solid', outlineColor: option.tone, outlineOffset: 2 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: option.tone, '& svg': { fontSize: 20 } }}>
                    {option.icon}
                    <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {option.title}
                    </Typography>
                  </Box>
                  <Typography component="span" variant="caption" color="text.secondary">
                    {option.text(who)}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>

          <TextField
            label="Name"
            value={form.name}
            onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
            autoFocus
            required
            fullWidth
            disabled={running}
            error={Boolean(errorOf('name'))}
            helperText={errorOf('name') ?? 'How it shows on the Lists screen and in campaigns.'}
            inputProps={{ maxLength: LIST_LIMITS.name + 20 }}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))}
            disabled={running}
            multiline
            minRows={2}
            fullWidth
            error={Boolean(errorOf('description'))}
            helperText={errorOf('description') ?? `Who this is and why. Optional. ${form.description.trim().length}/${LIST_LIMITS.description}`}
          />

          {refusal !== undefined && !refusedField && <Alert severity={changed ? 'warning' : 'error'}>{refusedText}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={running || count === undefined}
            startIcon={running ? <CircularProgress size={16} color="inherit" aria-label="Working" /> : undefined}
          >
            {kind === 'filter' ? 'Save filter' : `Make a list of ${who}`}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
